import { createContext } from 'react';

/**
 * Values the rendered form makes available to its blocks.
 * - `customValues`: the form's custom fields as they resolve in this browser
 *   (the author's default, replaced by the URL value where the field opts in).
 * - `crmTenantId`: the shared CRM tenant from `crmTenantId` in the form URL,
 *   defaulting to the form tenant.
 * - `crmJwtToken`: optional URL token used only by integrations that require it.
 * - `getAnswers`: reads what the visitor has entered so far, e.g. for the
 *   workflow preview.
 * - `pushAiConfig`: publishes an AiGenerator field's effective AI config
 *   (author defaults + respondent chip edits) under the reserved
 *   `_aiCfg:<field_name>` key so workflow variables resolve to it.
 */
export const FormRuntimeContext = createContext<{
  customValues: Record<string, string>;
  crmTenantId?: string;
  crmJwtToken?: string;
  /** The visitor's answers so far, keyed by field name (undefined outside the renderer). */
  getAnswers?: () => Record<string, any>;
  /**
   * Merges `{[_aiCfg:<field>]: config}` into the visitor's answers (undefined
   * outside the renderer). The form library never wires `props.onChange` to
   * custom elements, so this context writer is the only live channel for
   * reserved keys; regular field values keep flowing through hidden inputs.
   */
  pushAiConfig?: (fieldName: string, config: Record<string, string>) => void;
  /**
   * Moves a multi-step form on to its next step, with the same validation as
   * a Next button (undefined outside a renderer). No-op on the last step, so
   * an auto-advancing answer never submits the form by itself.
   */
  advanceStep?: () => void;
}>({ customValues: {} });
